/** Browser/runtime env for push diagnostics (injectable in tests). */
function getDefaultEnv() {
  if (typeof window !== "undefined") return window;
  return {};
}

function getDefaultSessionStorage() {
  if (typeof window !== "undefined") return window.sessionStorage;
  return undefined;
}

/** Push preference fields that imply the user wants mobile push delivery. */
const PUSH_PREF_KEYS = [
  "receiveAnomalyPush",
  "recieveCharacterizationPush",
  "recieveQualicheckPush",
  "receiveFillingPush",
  "receiveRotationPush",
];

const MAIL_SMS_PREF_KEYS = [
  "receiveAnomalyMail",
  "receiveAnomalySms",
  "receiveFillingMail",
  "receiveFillingSms",
  "receiveRotationMail",
  "receiveRotationSms",
  "receiveDowngradeMail",
  "recieveCharacterizationMail",
  "recieveCharacterizationSms",
  "recieveQualicheckMail",
  "recieveQualicheckSms",
  "receiveDailyPerformanceReport",
];

/**
 * If `updatedUser` is the logged-in session user, merge notification prefs
 * into localStorage so mobile settings stay in sync with the web Users matrix.
 */
export function syncSessionUserNotificationPrefs(updatedUser) {
  if (!updatedUser?._id || typeof localStorage === "undefined") {
    return null;
  }

  let current;
  try {
    current = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
  if (!current?._id || String(current._id) !== String(updatedUser._id)) {
    return null;
  }

  const next = { ...current };
  for (const key of [...PUSH_PREF_KEYS, ...MAIL_SMS_PREF_KEYS]) {
    if (Object.prototype.hasOwnProperty.call(updatedUser, key)) {
      next[key] = Boolean(updatedUser[key]);
    }
  }
  if (Array.isArray(updatedUser.fcmTokens)) {
    next.fcmTokens = updatedUser.fcmTokens;
  }
  if (typeof updatedUser.fcmTokenCount === "number") {
    next.fcmTokenCount = updatedUser.fcmTokenCount;
  }

  localStorage.setItem("user", JSON.stringify(next));
  return next;
}

/**
 * Merge a partial API user payload into the session user without dropping
 * fields omitted by endpoints like POST /access/fcmToken.
 */
export function mergeSessionUserPartial(partial) {
  if (!partial || typeof localStorage === "undefined") {
    return null;
  }

  let current;
  try {
    current = JSON.parse(localStorage.getItem("user") || "null") || {};
  } catch {
    current = {};
  }

  if (
    partial._id &&
    current._id &&
    String(partial._id) !== String(current._id)
  ) {
    return null;
  }

  const next = { ...current };
  for (const [key, value] of Object.entries(partial)) {
    if (value !== undefined) {
      next[key] = value;
    }
  }
  next.token = current.token || partial.token || next.token;
  localStorage.setItem("user", JSON.stringify(next));
  return next;
}

export const PUSH_HEALTH = {
  IDLE: "idle",
  HEALTHY: "healthy",
  NEEDS_PERMISSION: "needs_permission",
  BLOCKED: "blocked",
  UNSUPPORTED: "unsupported",
  IOS_INSTALL_REQUIRED: "ios_install_required",
  TOKEN_MISSING: "token_missing",
};

const PUSH_HEALTH_SESSION_KEY = "pushHealthHomeBannerDismissed";

export function hasAnyPushPref(user) {
  if (!user) return false;
  return PUSH_PREF_KEYS.some((key) => Boolean(user[key]));
}

export function hasRegisteredPushDevice(user) {
  if (!user) return false;
  if (Array.isArray(user.fcmTokens) && user.fcmTokens.some(Boolean)) {
    return true;
  }
  if (typeof user.fcmToken === "string" && user.fcmToken.trim()) {
    return true;
  }
  if (typeof user.fcmTokenCount === "number" && user.fcmTokenCount > 0) {
    return true;
  }
  return false;
}

/** CS status: prefs ON but no device token registered yet. */
export function isPushPendingDevice(user) {
  return hasAnyPushPref(user) && !hasRegisteredPushDevice(user);
}

export function isPushSupported(env = getDefaultEnv()) {
  if (!env || typeof env !== "object") return false;
  if (!("Notification" in env)) return false;
  if (!env.navigator || !("serviceWorker" in env.navigator)) return false;
  if (!("PushManager" in env)) return false;
  if (env.isSecureContext === false) return false;
  return true;
}

export function isIosDevice(env = getDefaultEnv()) {
  const nav = env?.navigator;
  if (!nav) return false;
  const ua = nav.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS may report as MacIntel with touch
  if (nav.platform === "MacIntel" && (nav.maxTouchPoints || 0) > 1) {
    return true;
  }
  return false;
}

export function isStandaloneDisplay(env = getDefaultEnv()) {
  if (!env) return false;
  const nav = env.navigator;
  if (nav && nav.standalone === true) return true;
  if (typeof env.matchMedia === "function") {
    try {
      if (env.matchMedia("(display-mode: standalone)").matches) return true;
    } catch {
      // ignore
    }
  }
  return false;
}

/**
 * Diagnose whether push intent can be delivered on this browser/device.
 * @returns {{ status: string, unhealthy: boolean }}
 */
export function getPushHealth({
  user,
  fcmTokenCookie,
  permission,
  env = getDefaultEnv(),
} = {}) {
  if (!hasAnyPushPref(user)) {
    return { status: PUSH_HEALTH.IDLE, unhealthy: false };
  }

  if (isIosDevice(env) && !isStandaloneDisplay(env)) {
    return { status: PUSH_HEALTH.IOS_INSTALL_REQUIRED, unhealthy: true };
  }

  if (!isPushSupported(env)) {
    return { status: PUSH_HEALTH.UNSUPPORTED, unhealthy: true };
  }

  const resolvedPermission =
    permission ??
    (env.Notification && typeof env.Notification.permission === "string"
      ? env.Notification.permission
      : "default");

  if (resolvedPermission === "denied") {
    return { status: PUSH_HEALTH.BLOCKED, unhealthy: true };
  }

  if (resolvedPermission === "default") {
    return { status: PUSH_HEALTH.NEEDS_PERMISSION, unhealthy: true };
  }

  if (!fcmTokenCookie) {
    return { status: PUSH_HEALTH.TOKEN_MISSING, unhealthy: true };
  }

  return { status: PUSH_HEALTH.HEALTHY, unhealthy: false };
}

export function getPushHealthMessageKey(status) {
  switch (status) {
    case PUSH_HEALTH.BLOCKED:
      return "push.health.blocked";
    case PUSH_HEALTH.NEEDS_PERMISSION:
      return "push.health.needsPermission";
    case PUSH_HEALTH.UNSUPPORTED:
      return "push.notifications.unsupported";
    case PUSH_HEALTH.IOS_INSTALL_REQUIRED:
      return "push.health.iosInstall";
    case PUSH_HEALTH.TOKEN_MISSING:
      return "push.health.tokenMissing";
    default:
      return null;
  }
}

export function isHomePushBannerDismissed(
  storage = getDefaultSessionStorage(),
) {
  try {
    return storage?.getItem(PUSH_HEALTH_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissHomePushBanner(storage = getDefaultSessionStorage()) {
  try {
    storage?.setItem(PUSH_HEALTH_SESSION_KEY, "1");
  } catch {
    // ignore quota / private mode
  }
}
